import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	getGraftsToAssigned,
	getUnAssignedTissueIds,
	newUnAssignedGraft,
	updateUnAssignedGraft,
} from '@/services/graftsToAssigned';
import { getProductSizes } from '@/services/products';
import type { FilterType } from '@/types/table';

export const useGetGraftsToAssign = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	globalFilters = '[]',
	sort = '',
	port = ''
}: FilterType) => {
	const payload = {
		page,
		limit,
		columnsFilters,
		search,
		globalFilters,
		sort: sort || '',
		port, 
	};

	return useQuery({
		queryKey: ['grafts-to-assign', payload],
		queryFn: () => getGraftsToAssigned(payload),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetGraftsProductSizes = ({
	product,
	enabled = true,
}: {
	product: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['grafts-to-assign', product],
		queryFn: () =>
			getProductSizes(product).then((res) =>
				res?.items?.map((item: string) => ({
					label: item,
					value: item,
				})),
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useGetUnAssignedTissueIds = ({
	clinicId,
	enabled = true,
}: {
	clinicId: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['grafts-to-assign', clinicId],
		queryFn: () =>
			getUnAssignedTissueIds(clinicId).then((res) =>
				res?.items.map((item: { tissueid: string }) => ({
					label: item.tissueid,
					value: item.tissueid,
				})),
			),
		enabled,
		keepPreviousData: false,
	});
};

export const useCreateUnAssignedGraft = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: newUnAssignedGraft,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['grafts-to-assign'] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
			toast.error(
				(error as any)?.response?.data?.error?.message ||
					(error as any)?.response?.data?.error?.message?.error?.message ||
					'Failed to create unassigned graft. Please try again.',
			);
		},
	});
};

export const useUpdateUnAssignedGraft = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateUnAssignedGraft,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['grafts-to-assign'] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
			toast.error(
				(error as any)?.response?.data?.error?.message?.error?.message ||
					'Failed to assign unassigned graft. Please try again.',
			);
		},
	});
};
